import React, { useEffect } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import { ThemeProvider } from '@mui/material/styles'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import usePlannerAuthorization from '../generic/hooks/useAuthorization/usePlannerAuthorization'
import useMachineDetail from './useMachineDetail'
import MachineDetailHeader, { MachineDetailSimpleHead } from './MachineDetailHeader'
import MachineDetailKpis from './MachineDetailKpis'
import MachineSiblingGroups from './MachineSiblingGroups'
import MachineWeeklyTable from './MachineWeeklyTable'
import MachineFabricationOrdersList from './MachineFabricationOrdersList'
import MachineCalendarBlock from './MachineCalendarBlock'
import MachineDetailSection from './MachineDetailSection'
import LoadCapacityChart from './LoadCapacityChart'
import { MACHINE_DETAIL_PALETTE, machineDetailTheme } from './machineDetailPalette'
import './machineDetailDrawer.css'

// Contract §7 -- frozen by Jupiter: right-anchored MUI Drawer, this width, with its scrim.
const DRAWER_WIDTH = 'min(560px, 94vw)'

// S109/CU16 -- Sun's stable `code`s with a literal of their own (both reused from story 153); anything
// else, including an unknown code, falls back to the drawer's generic literal. The raw code never shows.
const ERROR_MESSAGE_KEY_BY_CODE = {
  DEVICE_NOT_ELIGIBLE: 'Device not found, inactive or forecast',
  HORIZON_TOO_LONG: 'Plan horizon exceeds the 104 week limit'
}
const GENERIC_ERROR_KEY = 'The machine detail could not be calculated'
// RN8 bis -- the plan is gone: the drawer closes, with no error of its own and no retry.
const PLAN_NOT_FOUND = 'PLAN_NOT_FOUND'
// S112 -- a permission answer is not retried: the same request would be refused again.
const PERMISSION_STATUSES = [401, 403]

// S91 -- "Load per week": the load-capacity block's own SVG chart (contract §7, no new dependency) fed
// with `weeks` as it arrived, so the drawer and the block can never draw the same series differently.
// The "W40–W47 · the line is the OEE capacity" caption was removed at the PO's request (2026-09-29).
const MachineWeeklyChart = ({ detail }) => {
  const { t_ } = useSmartTranslation()
  return (
    <MachineDetailSection testId='machine-detail-chart' title={t_('Load per week')}>
      <div className='mdd__tile'>
        <LoadCapacityChart buckets={detail.weeks} showOeeLoss={false} />
      </div>
    </MachineDetailSection>
  )
}

// S109/S110/S112 -- a failed read replaces the figures (stale ones are never shown, CU7/S79 of story 153)
// but never takes the Planner down: the rest of the screen keeps working (P3).
const MachineDetailError = ({ error, onRetry, onClose }) => {
  const { t_ } = useSmartTranslation()
  const messageKey = ERROR_MESSAGE_KEY_BY_CODE[error.data?.code] ?? GENERIC_ERROR_KEY
  const canRetry = !PERMISSION_STATUSES.includes(error.status)
  return (
    <Alert
      severity='error'
      variant='outlined'
      data-testid='machine-detail-error'
      sx={{ borderRadius: '12px', color: MACHINE_DETAIL_PALETTE.text }}
      action={(
        <Stack direction='row' spacing={1}>
          {canRetry ? <Button color='inherit' size='small' onClick={onRetry}>{t_('Retry')}</Button> : null}
          <Button color='inherit' size='small' onClick={onClose}>{t_('Close')}</Button>
        </Stack>
      )}
    >
      {t_(messageKey)}
    </Alert>
  )
}

/**
 * Story 175, CU5-CU7 -- the detail of one machine, opened from its row label in the Gantt (D-1) as a
 * drawer over the Planner (D-2). It owns no state of the Planner: which machine is open, which
 * fabrication order is selected and when the plan changed all come from `Planner.jsx`, and every figure
 * comes from `GET planMetrics/{planificationId}/devices/{deviceId}` exactly as Sun sent it (INV-I).
 * Dressed like the job detail panel (`calendarSidePanel.css`): see `machineDetailDrawer.css`.
 */
const MachineDetailDrawer = ({
  planificationId, deviceId, plannerFilters, filterLabels, metricsVersion,
  selectedFabricationOrderId, onFabricationOrderClick, onMachineClick, onClose
}) => {
  const isUserPlannerAuthorized = usePlannerAuthorization()
  const isOpen = deviceId !== null && deviceId !== undefined
  const { data, error, reload } = useMachineDetail({
    planificationId,
    deviceId,
    plannerFilters,
    metricsVersion,
    enabled: isUserPlannerAuthorized('PLANNER')
  })
  const errorCode = error ? error.data?.code : undefined

  useEffect(() => {
    if (isOpen && errorCode === PLAN_NOT_FOUND) {
      onClose()
    }
  }, [isOpen, errorCode, onClose])

  let head
  let body
  if (error && errorCode !== PLAN_NOT_FOUND) {
    head = <MachineDetailSimpleHead onClose={onClose} />
    body = <MachineDetailError error={error} onRetry={reload} onClose={onClose} />
  } else if (data) {
    head = <MachineDetailHeader detail={data} planificationId={planificationId} plannerFilters={plannerFilters} filterLabels={filterLabels} onClose={onClose} />
    body = (
      <>
        <MachineDetailKpis detail={data} />
        <MachineWeeklyChart detail={data} />
        <MachineWeeklyTable weeks={data.weeks} hasCalendar={data.device.calendarId !== null && data.device.calendarId !== undefined} />
        <MachineFabricationOrdersList detail={data} selectedFabricationOrderId={selectedFabricationOrderId} onFabricationOrderClick={onFabricationOrderClick} />
        <MachineSiblingGroups siblingGroups={data.siblingGroups} onMachineClick={onMachineClick} />
        <MachineCalendarBlock detail={data} />
      </>
    )
  } else {
    head = <MachineDetailSimpleHead onClose={onClose} />
    body = (
      <LinearProgress
        color='success'
        sx={{ backgroundColor: MACHINE_DETAIL_PALETTE.surfaceHigh, '& .MuiLinearProgress-bar': { backgroundColor: MACHINE_DETAIL_PALETTE.accent } }}
      />
    )
  }

  return (
    <Drawer
      anchor='right'
      open={isOpen}
      onClose={onClose}
      data-testid='machine-detail-drawer'
      PaperProps={{ className: 'mdd', sx: { width: DRAWER_WIDTH } }}
    >
      {/* S175: dark on any theme -- the MUI pieces inside read the drawer palette, not the page's. */}
      <ThemeProvider theme={machineDetailTheme}>
        {head}
        <div className='mdd__body'>
          {body}
        </div>
      </ThemeProvider>
    </Drawer>
  )
}

export default MachineDetailDrawer
